import { useState, useCallback } from "react";
import { DebitNoteDataEntity } from "../../domain/entities/DebitNoteEntity";
import { CreateZohoDebitNoteUseCase } from "../../domain/use-cases/CreateZohoDebitNoteUseCase";
import { UpdateZohoDebitNoteUseCase } from "../../domain/use-cases/UpdateZohoDebitNoteUseCase";
import { UpdateDebitNoteJsonUseCase } from "../../domain/use-cases/UpdateDebitNoteJsonUseCase";
import { RejectDebitNoteUseCase } from "../../domain/use-cases/RejectDebitNoteUseCase";
import { ApproveDebitNoteUseCase } from "../../domain/use-cases/ApproveDebitNoteUseCase";
import { SaveDebitNoteAsDraftUseCase } from "../../domain/use-cases/SaveDebitNoteAsDraftUseCase";
import { DebitNoteRepositoryImpl } from "../../data/repositories/DebitNoteRepositoryImpl";
import { DebitNoteEntity } from "../../domain/entities/DebitNoteEntity";

// Create repository instance (singleton)
const debitNoteRepository = new DebitNoteRepositoryImpl();

// Create use cases
const createZohoDebitNoteUseCase = new CreateZohoDebitNoteUseCase(
  debitNoteRepository
);
const updateZohoDebitNoteUseCase = new UpdateZohoDebitNoteUseCase(
  debitNoteRepository
);
const updateDebitNoteJsonUseCase = new UpdateDebitNoteJsonUseCase(
  debitNoteRepository
);
const rejectDebitNoteUseCase = new RejectDebitNoteUseCase(
  debitNoteRepository
);
const approveDebitNoteUseCase = new ApproveDebitNoteUseCase(
  debitNoteRepository
);
const saveDebitNoteAsDraftUseCase = new SaveDebitNoteAsDraftUseCase(
  debitNoteRepository
);

export interface UseDebitNoteOperationsReturn {
  loading: boolean;
  error: string | null;

  createZohoDebitNote: (
    data: DebitNoteDataEntity,
    pdfUrl?: string,
    debitNoteId?: number
  ) => Promise<boolean>;
  updateZohoDebitNote: (
    debitNoteId: number,
    data: DebitNoteDataEntity
  ) => Promise<boolean>;
  updateDebitNoteJson: (
    debitNoteId: number,
    data: DebitNoteDataEntity
  ) => Promise<boolean>;
  rejectDebitNote: (debitNoteId: number) => Promise<boolean>;
  approveDebitNote: (debitNoteId: number) => Promise<boolean>;
  saveDebitNoteAsDraft: (
    data: DebitNoteDataEntity,
    options?: {
      jobid?: string | null;
      folder_id?: string | null;
      company_id?: number | null;
      gst_rates?: any;
      gst_types?: any;
      is_tds_applied?: number;
      tds_tax_id?: string | null;
      tds_tax_amount?: number | null;
      tds_tax_percentage?: number | null;
      tds_tax_name?: string | null;
      tds_section?: string | null;
    }
  ) => Promise<DebitNoteEntity | null>;

  clearError: () => void;
}

export const useDebitNoteOperations = (): UseDebitNoteOperationsReturn => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Create debit note in Zoho Books
   * If debitNoteId is provided, updates the existing debit note instead of creating a new one
   */
  const createZohoDebitNote = useCallback(
    async (
      data: DebitNoteDataEntity,
      pdfUrl?: string,
      debitNoteId?: number
    ): Promise<boolean> => {
      setLoading(true);
      setError(null);

      try {
        const result = await createZohoDebitNoteUseCase.execute(
          data,
          pdfUrl,
          debitNoteId
        );

        if (!result.success) {
          throw new Error(
            result.error || "Failed to create debit note in Zoho"
          );
        }

        return result.success;
      } catch (error: any) {
        const errorMessage =
          error.message || "Failed to create debit note in Zoho";
        setError(errorMessage);
        console.error("Failed to create Zoho debit note:", error);
        return false;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  /**
   * Update debit note in Zoho Books
   */
  const updateZohoDebitNote = useCallback(
    async (
      debitNoteId: number,
      data: DebitNoteDataEntity
    ): Promise<boolean> => {
      setLoading(true);
      setError(null);

      try {
        const result = await updateZohoDebitNoteUseCase.execute(
          debitNoteId,
          data
        );

        if (!result.success) {
          throw new Error(
            result.error || "Failed to update debit note in Zoho"
          );
        }

        return result.success;
      } catch (error: any) {
        const errorMessage =
          error.message || "Failed to update debit note in Zoho";
        setError(errorMessage);
        console.error("Failed to update Zoho debit note:", error);
        return false;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  /**
   * Update debit note JSON data (for auto-save and manual edits)
   */
  const updateDebitNoteJson = useCallback(
    async (
      debitNoteId: number,
      data: DebitNoteDataEntity
    ): Promise<boolean> => {
      setLoading(true);
      setError(null);

      try {
        const result = await updateDebitNoteJsonUseCase.execute(
          debitNoteId,
          data
        );

        if (!result.success) {
          throw new Error(result.error || "Failed to update debit note JSON");
        }

        return true;
      } catch (error: any) {
        const errorMessage =
          error.message || "Failed to update debit note JSON";
        setError(errorMessage);
        console.error("Failed to update debit note JSON:", error);
        return false;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  /**
   * Reject a debit note
   */
  const rejectDebitNote = useCallback(
    async (debitNoteId: number): Promise<boolean> => {
      setLoading(true);
      setError(null);

      try {
        const result = await rejectDebitNoteUseCase.execute(debitNoteId);

        if (!result.success) {
          throw new Error(result.error || "Failed to reject debit note");
        }

        return true;
      } catch (error: any) {
        const errorMessage = error.message || "Failed to reject debit note";
        setError(errorMessage);
        console.error("Failed to reject debit note:", error);
        return false;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  /**
   * Approve a debit note
   */
  const approveDebitNote = useCallback(
    async (debitNoteId: number): Promise<boolean> => {
      setLoading(true);
      setError(null);

      try {
        const result = await approveDebitNoteUseCase.execute(debitNoteId);

        if (!result.success) {
          throw new Error(result.error || "Failed to approve debit note");
        }

        return true;
      } catch (error: any) {
        const errorMessage = error.message || "Failed to approve debit note";
        setError(errorMessage);
        console.error("Failed to approve debit note:", error);
        return false;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  /**
   * Save debit note as draft
   */
  const saveDebitNoteAsDraft = useCallback(
    async (
      data: DebitNoteDataEntity,
      options?: {
        jobid?: string | null;
        folder_id?: string | null;
        company_id?: number | null;
        gst_rates?: any;
        gst_types?: any;
        is_tds_applied?: number;
        tds_tax_id?: string | null;
        tds_tax_amount?: number | null;
        tds_tax_percentage?: number | null;
        tds_tax_name?: string | null;
        tds_section?: string | null;
      }
    ): Promise<DebitNoteEntity | null> => {
      setLoading(true);
      setError(null);

      try {
        const result = await saveDebitNoteAsDraftUseCase.execute(
          data,
          options
        );

        return result;
      } catch (error: any) {
        const errorMessage =
          error.message || "Failed to save debit note as draft";
        setError(errorMessage);
        console.error("Failed to save debit note as draft:", error);
        return null;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  /**
   * Clear error
   */
  const clearError = useCallback((): void => {
    setError(null);
  }, []);

  return {
    loading,
    error,
    createZohoDebitNote,
    updateZohoDebitNote,
    updateDebitNoteJson,
    rejectDebitNote,
    approveDebitNote,
    saveDebitNoteAsDraft,
    clearError,
  };
};

